import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const MODES = ["Video", "Phone", "In-person"];

export const InterviewScheduler = ({ candidate, interview, onClose }) => {
  const qc = useQueryClient();
  const open = !!(candidate || interview);
  const [f, setF] = useState(null);
  const init = {
    scheduled_at: interview?.scheduled_at ? interview.scheduled_at.slice(0, 16) : "",
    duration_min: interview?.duration_min ?? 45, mode: interview?.mode || "Video",
    location: interview?.location || "", interviewer: interview?.interviewer || "", notes: interview?.notes || "",
  };
  const v = f || init;
  const set = (k, val) => setF({ ...v, [k]: val });
  const close = () => { setF(null); onClose(); };

  const mut = useMutation({
    mutationFn: async () => {
      const body = { ...v, duration_min: Number(v.duration_min) };
      if (interview) return (await api.patch(`/interviews/${interview.id}`, body)).data;
      return (await api.post("/interviews", { ...body, candidate_id: candidate.id })).data;
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["interviews"] }); qc.invalidateQueries({ queryKey: ["candidates"] });
      toast.success(d.emailed ? `Interview ${interview ? "updated" : "scheduled"} — invite emailed to candidate` : `Interview ${interview ? "updated" : "scheduled"} (email not delivered)`);
      close();
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg" data-testid="interview-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{interview ? "Reschedule / Edit Interview" : "Schedule Interview"}</DialogTitle>
          <DialogDescription>{(interview || candidate)?.candidate_name || candidate?.name} — {(interview || candidate)?.role}. The candidate receives an email with calendar links.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="space-y-1.5"><Label>Date & Time (IST)</Label><Input type="datetime-local" value={v.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} data-testid="interview-datetime-input" /></div>
          <div className="space-y-1.5"><Label>Duration (min)</Label><Input type="number" min="10" max="480" value={v.duration_min} onChange={(e) => set("duration_min", e.target.value)} data-testid="interview-duration-input" /></div>
          <div className="space-y-1.5"><Label>Mode</Label>
            <Select value={v.mode} onValueChange={(m) => set("mode", m)}>
              <SelectTrigger data-testid="interview-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Interviewer</Label><Input value={v.interviewer} onChange={(e) => set("interviewer", e.target.value)} data-testid="interview-interviewer-input" /></div>
          <div className="space-y-1.5 col-span-2"><Label>{v.mode === "In-person" ? "Office address" : "Meeting link / Phone"}</Label><Input value={v.location} onChange={(e) => set("location", e.target.value)} data-testid="interview-location-input" placeholder={v.mode === "Video" ? "https://meet.google.com/…" : ""} /></div>
          <div className="space-y-1.5 col-span-2"><Label>Notes for candidate</Label><Input value={v.notes} onChange={(e) => set("notes", e.target.value)} data-testid="interview-notes-input" /></div>
        </div>
        <DialogFooter>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !v.scheduled_at} className="rounded-lg" data-testid="interview-save-button">
            {mut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : interview ? "Save & Notify" : "Schedule & Send Invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
